// Shift.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Shift = () => {
    const [shifts, setShifts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedShift, setSelectedShift] = useState(null);

    // Updated form data to include all fields from database
    const [formData, setFormData] = useState({
        shift_name: '',
        start_time: '09:00',
        end_time: '18:00',
        working_hours: 8.00,
        break_mts: 60,
        grace_in_mts: 10,
        grace_out_mts: 10,
        min_halfday_hours: 4.00,
        min_fullday_hours: 8,
        allow_late_count: 3,
        late_deduction_type: 'NONE',
        night_shift: 0,
        // Punch window fields
        in_window_before: 60,
        in_window_after: 60,
        out_window_before: 60,
        out_window_after: 120
    });

    const [validationErrors, setValidationErrors] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');

    // User permissions state
    const [userPermissions, setUserPermissions] = useState({
        can_view: false,
        can_add: false,
        can_edit: false,
        can_delete: false,
        can_export: false,
        loaded: false
    });

    const navigate = useNavigate();
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    // Fetch user permissions function
    const fetchUserPermissions = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                setUserPermissions({
                    can_view: false,
                    can_add: false,
                    can_edit: false,
                    can_delete: false,
                    can_export: false,
                    loaded: true
                });
                return;
            }

            const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success && response.data.permissions) {
                const shiftPermissions = response.data.permissions.find(
                    p => p.module_name === 'Shift'
                );

                if (shiftPermissions) {
                    setUserPermissions({
                        can_view: Boolean(shiftPermissions.can_view),
                        can_add: Boolean(shiftPermissions.can_add),
                        can_edit: Boolean(shiftPermissions.can_edit),
                        can_delete: Boolean(shiftPermissions.can_delete),
                        can_export: Boolean(shiftPermissions.can_export),
                        loaded: true
                    });
                } else {
                    setUserPermissions({
                        can_view: false,
                        can_add: false,
                        can_edit: false,
                        can_delete: false,
                        can_export: false,
                        loaded: true
                    });
                }
            } else {
                setUserPermissions({
                    can_view: false,
                    can_add: false,
                    can_edit: false,
                    can_delete: false,
                    can_export: false,
                    loaded: true
                });
            }
        } catch (error) {
            console.error('Error fetching permissions:', error);
            setUserPermissions({
                can_view: false,
                can_add: false,
                can_edit: false,
                can_delete: false,
                can_export: false,
                loaded: true
            });
        }
    }, [API_BASE]);

    const fetchShifts = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            if (!token) {
                setError('No authentication token found');
                setLoading(false);
                return;
            }

            const response = await axios.get(`${API_BASE}/get_shifts.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                timeout: 10000
            });

            if (response.data && response.data.success) {
                setShifts(response.data.shifts || []);
                setError('');
            } else {
                setShifts([]);
                const errorMsg = response.data?.error || 'Failed to fetch shifts';
                setError(errorMsg);
            }
        } catch (error) {
            setShifts([]);
            const errorMsg = error.response?.data?.error ||
                error.message ||
                'Failed to fetch shifts';
            setError(errorMsg);
            console.error('Error fetching shifts:', error);
        } finally {
            setLoading(false);
        }
    }, [API_BASE]);

    useEffect(() => {
        const initializeData = async () => {
            await fetchUserPermissions();
            await fetchShifts();
        };

        initializeData();
    }, [fetchUserPermissions, fetchShifts]);

    // Permission check functions
    const canViewShifts = () => userPermissions.can_view;
    const canAddShift = () => userPermissions.can_add;
    const canEditShift = () => userPermissions.can_edit;
    const canDeleteShift = () => userPermissions.can_delete;
    const canExportShifts = () => userPermissions.can_export;

    const handleAdd = () => {
        if (!canAddShift()) {
            alert('You do not have permission to add shifts');
            return;
        }
        setFormData({
            shift_name: '',
            start_time: '09:00',
            end_time: '18:00',
            working_hours: 8.00,
            break_mts: 60,
            grace_in_mts: 10,
            grace_out_mts: 10,
            min_halfday_hours: 4.00,
            min_fullday_hours: 8,
            allow_late_count: 3,
            late_deduction_type: 'NONE',
            night_shift: 0,
            in_window_before: 60,
            in_window_after: 60,
            out_window_before: 60,
            out_window_after: 120
        });
        setValidationErrors({});
        setShowAddModal(true);
    };

    const handleEdit = (shift) => {
        if (!canEditShift()) {
            alert('You do not have permission to edit shifts');
            return;
        }
        setSelectedShift(shift);
        setFormData({
            shift_name: shift.shift_name,
            start_time: shift.start_time.substring(0, 5),
            end_time: shift.end_time.substring(0, 5),
            working_hours: parseFloat(shift.working_hours),
            break_mts: parseInt(shift.break_mts),
            grace_in_mts: parseInt(shift.grace_in_mts),
            grace_out_mts: parseInt(shift.grace_out_mts),
            min_halfday_hours: parseFloat(shift.min_halfday_hours),
            min_fullday_hours: parseInt(shift.min_fullday_hours),
            allow_late_count: parseInt(shift.allow_late_count),
            late_deduction_type: shift.late_duction_type || 'NONE',
            night_shift: shift.night_shift ? 1 : 0,
            in_window_before: parseInt(shift.in_window_before) || 60,
            in_window_after: parseInt(shift.in_window_after) || 60,
            out_window_before: parseInt(shift.out_window_before) || 60,
            out_window_after: parseInt(shift.out_window_after) || 120
        });
        setValidationErrors({});
        setShowEditModal(true);
    };

    const handleDelete = (shift) => {
        if (!canDeleteShift()) {
            alert('You do not have permission to delete shifts');
            return;
        }
        setSelectedShift(shift);
        setShowDeleteModal(true);
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;

        let processedValue = value;
        if (type === 'number') {
            processedValue = value === '' ? '' : parseFloat(value);
        } else if (type === 'checkbox') {
            processedValue = e.target.checked ? 1 : 0;
        }

        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));

        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: '' }));
        }

        // Auto-calculate working hours if start or end time changes
        if (name === 'start_time' || name === 'end_time' || name === 'break_mts') {
            setTimeout(() => calculateWorkingHours(), 100);
        }
    };

    // Calculate working hours based on start time, end time, and break
    const calculateWorkingHours = () => {
        if (!formData.start_time || !formData.end_time) return;

        const start = new Date(`2000-01-01T${formData.start_time}`);
        const end = new Date(`2000-01-01T${formData.end_time}`);

        let diff = end - start;
        if (diff < 0) {
            diff += 24 * 60 * 60 * 1000; // Add 24 hours if crossing midnight
        }

        const totalMinutes = diff / (1000 * 60);
        const breakMinutes = formData.break_mts || 0;
        const workingMinutes = totalMinutes - breakMinutes;
        const workingHours = (workingMinutes / 60).toFixed(2);

        setFormData(prev => ({
            ...prev,
            working_hours: parseFloat(workingHours)
        }));
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.shift_name.trim()) {
            errors.shift_name = 'Shift name is required';
        } else if (formData.shift_name.length > 50) {
            errors.shift_name = 'Shift name cannot exceed 50 characters';
        }

        if (!formData.start_time) {
            errors.start_time = 'Start time is required';
        }

        if (!formData.end_time) {
            errors.end_time = 'End time is required';
        }

        // Validate that end time is after start time
        if (formData.start_time && formData.end_time) {
            const start = new Date(`2000-01-01T${formData.start_time}`);
            const end = new Date(`2000-01-01T${formData.end_time}`);
            let diff = end - start;
            if (diff < 0) diff += 24 * 60 * 60 * 1000;

            if (diff <= 0) {
                errors.end_time = 'End time must be after start time';
            }
        }

        // Validate numeric fields
        if (formData.working_hours < 0 || formData.working_hours > 24) {
            errors.working_hours = 'Working hours must be between 0 and 24';
        }

        if (formData.break_mts < 0 || formData.break_mts > 480) {
            errors.break_mts = 'Break minutes must be between 0 and 480';
        }

        if (formData.grace_in_mts < 0 || formData.grace_in_mts > 120) {
            errors.grace_in_mts = 'Grace in minutes must be between 0 and 120';
        }

        if (formData.grace_out_mts < 0 || formData.grace_out_mts > 120) {
            errors.grace_out_mts = 'Grace out minutes must be between 0 and 120';
        }

        if (formData.min_halfday_hours < 0 || formData.min_halfday_hours > formData.working_hours) {
            errors.min_halfday_hours = 'Minimum half day hours must be between 0 and working hours';
        }

        if (formData.min_fullday_hours < formData.min_halfday_hours || formData.min_fullday_hours > formData.working_hours) {
            errors.min_fullday_hours = 'Minimum full day hours must be between half day hours and working hours';
        }

        if (formData.allow_late_count < 0 || formData.allow_late_count > 31) {
            errors.allow_late_count = 'Allowed late count must be between 0 and 31';
        }

        if (formData.in_window_before < 0 || formData.in_window_before > 240) {
            errors.in_window_before = 'In window (before) must be between 0 and 240 minutes';
        }

        if (formData.in_window_after < 0 || formData.in_window_after > 240) {
            errors.in_window_after = 'In window (after) must be between 0 and 240 minutes';
        }

        if (formData.out_window_before < 0 || formData.out_window_before > 240) {
            errors.out_window_before = 'Out window (before) must be between 0 and 240 minutes';
        }

        if (formData.out_window_after < 0 || formData.out_window_after > 240) {
            errors.out_window_after = 'Out window (after) must be between 0 and 240 minutes';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const calculateDuration = (start, end) => {
        const startTime = new Date(`2000-01-01T${start}`);
        const endTime = new Date(`2000-01-01T${end}`);

        let diff = endTime - startTime;
        if (diff < 0) {
            diff += 24 * 60 * 60 * 1000;
        }

        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        return `${hours}h ${minutes}m`;
    };

    const getShiftType = (start, end, nightShift) => {
        if (nightShift) return 'Night';

        const startHour = parseInt(start.split(':')[0]);

        if (startHour >= 6 && startHour < 14) {
            return 'Morning';
        } else if (startHour >= 14 && startHour < 22) {
            return 'Evening';
        } else {
            return 'Night';
        }
    };

    const getShiftTypeColor = (shiftType) => {
        const colors = {
            'Morning': 'bg-success',
            'Evening': 'bg-warning',
            'Night': 'bg-dark',
            'General': 'bg-primary'
        };
        return colors[shiftType] || 'bg-secondary';
    };

    const getLateDeductionTypeLabel = (type) => {
        const labels = {
            'NONE': 'No Deduction',
            'HALF_DAY': 'Half Day Deduction',
            'LOP': 'Loss of Pay'
        };
        return labels[type] || type;
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}/add_shift.php`,
                formData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchShifts();
                setShowAddModal(false);
                alert('Shift added successfully');
            } else {
                setError(response.data.error || 'Failed to add shift');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to add shift');
            console.error('Error adding shift:', error);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}/update_shift.php`,
                {
                    shift_id: selectedShift.shift_id,
                    ...formData
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchShifts();
                setShowEditModal(false);
                alert('Shift updated successfully');
            } else {
                setError(response.data.error || 'Failed to update shift');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to update shift');
            console.error('Error updating shift:', error);
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}/delete_shift.php`,
                { shift_id: selectedShift.shift_id },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchShifts();
                setShowDeleteModal(false);
                alert('Shift deleted successfully');
            } else {
                setError(response.data.error || 'Failed to delete shift');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to delete shift');
            console.error('Error deleting shift:', error);
        }
    };

    const filteredShifts = shifts.filter(shift => {
        return (
            shift.shift_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            shift.start_time?.includes(searchTerm) ||
            shift.end_time?.includes(searchTerm) ||
            shift.late_duction_type?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    const sortedShifts = [...filteredShifts].sort((a, b) => {
        switch (sortBy) {
            case 'name':
                return a.shift_name.localeCompare(b.shift_name);
            case 'start':
                return a.start_time.localeCompare(b.start_time);
            case 'duration':
                const durationA = calculateDuration(a.start_time, a.end_time);
                const durationB = calculateDuration(b.start_time, b.end_time);
                return durationB.localeCompare(durationA);
            default:
                return a.shift_id - b.shift_id;
        }
    });

    // Show loading while permissions are being fetched
    if (loading || !userPermissions.loaded) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Loading permissions...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // If user doesn't have view permission, show access denied
    if (!canViewShifts()) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                                <h3 className="mt-3 text-warning">Access Denied</h3>
                                <p className="text-muted">You do not have permission to view shifts.</p>
                                <button
                                    className="btn btn-primary mt-3"
                                    onClick={() => navigate('/dashboard')}
                                >
                                    <i className="bi bi-speedometer2 me-2"></i>
                                    Back to Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body py-4">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center">
                                        <div className="btn-group me-3" role="group">
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/admin-panel')}
                                                title="Go to Dashboard"
                                            >
                                                <i className="bi bi-speedometer2"></i>
                                            </button>
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/Menu')}
                                                title="Go to Menu"
                                            >
                                                <i className="bi bi-menu-button"></i>
                                            </button>
                                        </div>
                                        <div>
                                            <h2 className="h4 mb-1 fw-bold text-dark">
                                                <i className="bi bi-clock-history me-2 text-primary"></i>
                                                Shift Management
                                                {!canAddShift() && !canEditShift() && !canDeleteShift() && (
                                                    <span className="badge bg-warning ms-2">View Only</span>
                                                )}
                                            </h2>
                                            <p className="text-muted mb-0">
                                                Manage work shifts with detailed configurations
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 text-end">
                                    <div className="d-flex justify-content-end align-items-center">
                                        <button
                                            className="btn btn-outline-secondary me-2"
                                            onClick={() => navigate('/admin-panel')}
                                        >
                                            <i className="bi bi-arrow-left me-2"></i>
                                            Back to Dashboard
                                        </button>
                                        {canAddShift() && (
                                            <button
                                                className="btn btn-primary text-white"
                                                onClick={handleAdd}
                                            >
                                                <i className="bi bi-plus-circle me-2"></i>
                                                Add Shift
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Navigation */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card bg-light border-0">
                        <div className="card-body py-3">
                            <div className="row text-center">
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/admin-panel')}
                                    >
                                        <i className="bi bi-speedometer2 me-2"></i>
                                        Dashboard
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/employees')}
                                    >
                                        <i className="bi bi-people me-2"></i>
                                        Employees
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-info w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/policies')}
                                    >
                                        <i className="bi bi-file-earmark-text me-2"></i>
                                        Policies
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-warning w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/holidays')}
                                    >
                                        <i className="bi bi-calendar-event me-2"></i>
                                        Holidays
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-primary text-white w-100 d-flex align-items-center justify-content-center"
                                        disabled
                                    >
                                        <i className="bi bi-clock-history me-2"></i>
                                        Shifts
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-dark w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/Menu')}
                                    >
                                        <i className="bi bi-menu-button me-2"></i>
                                        Menu
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <div>
                                <strong>Error:</strong> {error}
                                <div className="mt-2">
                                    <button
                                        className="btn btn-sm btn-outline-danger me-2"
                                        onClick={fetchShifts}
                                    >
                                        <i className="bi bi-arrow-clockwise me-1"></i>
                                        Retry
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setError('')}
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Search and Action Bar */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row g-3 align-items-center">
                                <div className="col-md-4">
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="bi bi-search text-muted"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0"
                                            placeholder="Search shifts..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        {searchTerm && (
                                            <button
                                                className="btn btn-outline-secondary border-start-0"
                                                onClick={() => setSearchTerm('')}
                                            >
                                                <i className="bi bi-x"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-select"
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                    >
                                        <option value="name">Sort by Name</option>
                                        <option value="start">Sort by Start Time</option>
                                        <option value="duration">Sort by Duration</option>
                                    </select>
                                </div>
                                <div className="col-md-5 text-end">
                                    <div className="d-flex justify-content-end align-items-center">
                                        <button
                                            className="btn btn-outline-secondary me-2"
                                            onClick={fetchShifts}
                                            title="Refresh"
                                        >
                                            <i className="bi bi-arrow-clockwise"></i>
                                        </button>
                                        {canExportShifts() && (
                                            <button
                                                className="btn btn-outline-success me-2"
                                                onClick={() => alert('Export functionality coming soon!')}
                                                title="Export Shifts"
                                            >
                                                <i className="bi bi-download"></i>
                                            </button>
                                        )}
                                        {canAddShift() && (
                                            <button
                                                className="btn btn-primary text-white"
                                                onClick={handleAdd}
                                            >
                                                <i className="bi bi-plus-circle me-2"></i>
                                                Add Shift
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Shifts Table */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body p-0">
                            {sortedShifts.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-clock-history display-1 text-muted"></i>
                                    <h4 className="mt-3 text-muted">No shifts found</h4>
                                    <p className="text-muted">
                                        {shifts.length === 0
                                            ? "No shifts available. Add your first shift."
                                            : "No shifts match your search criteria."
                                        }
                                    </p>
                                    {shifts.length === 0 && canAddShift() && (
                                        <button
                                            className="btn btn-primary text-white mt-2"
                                            onClick={handleAdd}
                                        >
                                            <i className="bi bi-plus-circle me-2"></i>
                                            Add First Shift
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="ps-4">Shift Details</th>
                                                <th>Timings & Duration</th>
                                                <th>Configuration</th>
                                                <th className="text-end pe-4">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedShifts.map((shift) => {
                                                const shiftType = getShiftType(
                                                    shift.start_time,
                                                    shift.end_time,
                                                    shift.night_shift
                                                );
                                                const duration = calculateDuration(shift.start_time, shift.end_time);
                                                const startTime = shift.start_time.substring(0, 5);
                                                const endTime = shift.end_time.substring(0, 5);

                                                return (
                                                    <tr key={shift.shift_id} className="shift-row">
                                                        <td className="ps-4">
                                                            <div className="d-flex align-items-center">
                                                                <div className="shift-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                                                    style={{ width: '40px', height: '40px' }}
                                                                >
                                                                    <i className="bi bi-clock"></i>
                                                                </div>
                                                                <div>
                                                                    <div className="fw-semibold">
                                                                        {shift.shift_name}
                                                                        <div className="d-flex align-items-center mt-1">
                                                                            <span className={`badge ${getShiftTypeColor(shiftType)}`}>
                                                                                {shiftType} Shift
                                                                            </span>
                                                                            {shift.night_shift ? (
                                                                                <span className="badge bg-dark ms-2">🌙 Night</span>
                                                                            ) : (
                                                                                <span className="badge bg-info ms-2">☀️ Day</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <small className="text-muted">
                                                                        ID: {shift.shift_id}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center mb-1">
                                                                <div className="time-badge bg-success text-white rounded-pill px-3 py-1 me-2">
                                                                    <i className="bi bi-clock me-1"></i>
                                                                    {startTime}
                                                                </div>
                                                                <i className="bi bi-arrow-right text-muted me-2"></i>
                                                                <div className="time-badge bg-danger text-white rounded-pill px-3 py-1">
                                                                    <i className="bi bi-clock-fill me-1"></i>
                                                                    {endTime}
                                                                </div>
                                                            </div>
                                                            <div className="d-flex">
                                                                <small className="text-muted me-3">
                                                                    <i className="bi bi-stopwatch me-1"></i>
                                                                    {duration}
                                                                </small>
                                                                <small className="text-muted">
                                                                    <i className="bi bi-cup-hot me-1"></i>
                                                                    {shift.break_mts}m break
                                                                </small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex flex-wrap gap-1">
                                                                <span className={`badge ${getShiftTypeColor(shiftType)}`}>
                                                                    {shiftType}
                                                                </span>
                                                                <span className="badge bg-info">
                                                                    Grace: {shift.grace_in_mts}m
                                                                </span>
                                                                <span className="badge bg-warning">
                                                                    Late Allowed: {shift.allow_late_count}
                                                                </span>
                                                                <span className="badge bg-secondary">
                                                                    {getLateDeductionTypeLabel(shift.late_duction_type)}
                                                                </span>
                                                            </div>
                                                            <small className="text-muted d-block mt-1">
                                                                Min: {shift.min_halfday_hours}h half / {shift.min_fullday_hours}h full
                                                            </small>
                                                        </td>
                                                        <td className="text-end pe-4">
                                                            <div className="btn-group" role="group">
                                                                {canEditShift() && (
                                                                    <button
                                                                        className="btn btn-outline-primary btn-sm"
                                                                        onClick={() => handleEdit(shift)}
                                                                        title="Edit Shift"
                                                                    >
                                                                        <i className="bi bi-pencil"></i>
                                                                    </button>
                                                                )}
                                                                {canDeleteShift() && (
                                                                    <button
                                                                        className="btn btn-outline-danger btn-sm"
                                                                        onClick={() => handleDelete(shift)}
                                                                        title="Delete Shift"
                                                                    >
                                                                        <i className="bi bi-trash"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Footer */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card bg-light border-0">
                        <div className="card-body py-3">
                            <div className="row text-center">
                                <div className="col-md-3">
                                    <div className="fw-bold text-primary fs-4">{shifts.length}</div>
                                    <small className="text-muted">Total Shifts</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-success fs-4">
                                        {shifts.filter(s => getShiftType(s.start_time, s.end_time, s.night_shift) === 'Morning').length}
                                    </div>
                                    <small className="text-muted">Morning Shifts</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-warning fs-4">
                                        {shifts.filter(s => getShiftType(s.start_time, s.end_time, s.night_shift) === 'Evening').length}
                                    </div>
                                    <small className="text-muted">Evening Shifts</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-dark fs-4">
                                        {shifts.filter(s => getShiftType(s.start_time, s.end_time, s.night_shift) === 'Night').length}
                                    </div>
                                    <small className="text-muted">Night Shifts</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions Footer */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card border-0">
                        <div className="card-body text-center py-3">
                            <div className="btn-group" role="group">
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={fetchShifts}
                                >
                                    <i className="bi bi-arrow-clockwise me-2"></i>
                                    Refresh Data
                                </button>
                                <button
                                    className="btn btn-outline-success"
                                    onClick={() => navigate('/employees')}
                                >
                                    <i className="bi bi-people me-2"></i>
                                    Manage Employees
                                </button>
                                <button
                                    className="btn btn-outline-warning"
                                    onClick={() => navigate('/holidays')}
                                >
                                    <i className="bi bi-calendar-event me-2"></i>
                                    Manage Holidays
                                </button>
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => navigate('/policies')}
                                >
                                    <i className="bi bi-file-earmark-text me-2"></i>
                                    Attendance Policies
                                </button>
                                {canAddShift() && (
                                    <button
                                        className="btn btn-primary text-white"
                                        onClick={handleAdd}
                                    >
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Add New Shift
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Shift Modal */}
            {showAddModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header" style={{
                                background: 'linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)',
                                borderBottom: 'none'
                            }}>
                                <h5 className="modal-title text-white">
                                    <i className="bi bi-plus-circle me-2"></i>
                                    Add New Shift
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowAddModal(false)}
                                ></button>
                            </div>

                            <form onSubmit={handleAddSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        {/* Left Column - Basic Info */}
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="shift_name" className="form-label fw-medium">
                                                    Shift Name <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${validationErrors.shift_name ? 'is-invalid' : ''}`}
                                                    id="shift_name"
                                                    name="shift_name"
                                                    value={formData.shift_name}
                                                    onChange={handleChange}
                                                    placeholder="e.g., Morning Shift, Night Shift"
                                                    required
                                                />
                                                {validationErrors.shift_name && (
                                                    <div className="invalid-feedback">{validationErrors.shift_name}</div>
                                                )}
                                                <div className="form-text">Descriptive name for the shift</div>
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="start_time" className="form-label fw-medium">
                                                            Start Time <span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="time"
                                                            className={`form-control ${validationErrors.start_time ? 'is-invalid' : ''}`}
                                                            id="start_time"
                                                            name="start_time"
                                                            value={formData.start_time}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                        {validationErrors.start_time && (
                                                            <div className="invalid-feedback">{validationErrors.start_time}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="end_time" className="form-label fw-medium">
                                                            End Time <span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="time"
                                                            className={`form-control ${validationErrors.end_time ? 'is-invalid' : ''}`}
                                                            id="end_time"
                                                            name="end_time"
                                                            value={formData.end_time}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                        {validationErrors.end_time && (
                                                            <div className="invalid-feedback">{validationErrors.end_time}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="break_mts" className="form-label fw-medium">
                                                    Break Time (minutes)
                                                </label>
                                                <input
                                                    type="number"
                                                    className={`form-control ${validationErrors.break_mts ? 'is-invalid' : ''}`}
                                                    id="break_mts"
                                                    name="break_mts"
                                                    value={formData.break_mts}
                                                    onChange={handleChange}
                                                    min="0"
                                                    max="480"
                                                    step="15"
                                                />
                                                {validationErrors.break_mts && (
                                                    <div className="invalid-feedback">{validationErrors.break_mts}</div>
                                                )}
                                                <div className="form-text">Total break time in minutes</div>
                                            </div>

                                            <div className="mb-3 form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="night_shift"
                                                    name="night_shift"
                                                    checked={formData.night_shift === 1}
                                                    onChange={handleChange}
                                                />
                                                <label className="form-check-label" htmlFor="night_shift">
                                                    Night Shift
                                                </label>
                                                <div className="form-text">Check if this is a night shift</div>
                                            </div>
                                        </div>

                                        {/* Right Column - Configuration */}
                                        <div className="col-md-6">
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="grace_in_mts" className="form-label fw-medium">
                                                            Grace In (minutes)
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.grace_in_mts ? 'is-invalid' : ''}`}
                                                            id="grace_in_mts"
                                                            name="grace_in_mts"
                                                            value={formData.grace_in_mts}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="120"
                                                        />
                                                        {validationErrors.grace_in_mts && (
                                                            <div className="invalid-feedback">{validationErrors.grace_in_mts}</div>
                                                        )}
                                                        <div className="form-text">Late allowance at start</div>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="grace_out_mts" className="form-label fw-medium">
                                                            Grace Out (minutes)
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.grace_out_mts ? 'is-invalid' : ''}`}
                                                            id="grace_out_mts"
                                                            name="grace_out_mts"
                                                            value={formData.grace_out_mts}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="120"
                                                        />
                                                        {validationErrors.grace_out_mts && (
                                                            <div className="invalid-feedback">{validationErrors.grace_out_mts}</div>
                                                        )}
                                                        <div className="form-text">Early leave allowance</div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="min_halfday_hours" className="form-label fw-medium">
                                                            Min Half Day Hours
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.min_halfday_hours ? 'is-invalid' : ''}`}
                                                            id="min_halfday_hours"
                                                            name="min_halfday_hours"
                                                            value={formData.min_halfday_hours}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="12"
                                                            step="0.5"
                                                        />
                                                        {validationErrors.min_halfday_hours && (
                                                            <div className="invalid-feedback">{validationErrors.min_halfday_hours}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="min_fullday_hours" className="form-label fw-medium">
                                                            Min Full Day Hours
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.min_fullday_hours ? 'is-invalid' : ''}`}
                                                            id="min_fullday_hours"
                                                            name="min_fullday_hours"
                                                            value={formData.min_fullday_hours}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="24"
                                                            step="1"
                                                        />
                                                        {validationErrors.min_fullday_hours && (
                                                            <div className="invalid-feedback">{validationErrors.min_fullday_hours}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="allow_late_count" className="form-label fw-medium">
                                                    Allowed Late Count (per month)
                                                </label>
                                                <input
                                                    type="number"
                                                    className={`form-control ${validationErrors.allow_late_count ? 'is-invalid' : ''}`}
                                                    id="allow_late_count"
                                                    name="allow_late_count"
                                                    value={formData.allow_late_count}
                                                    onChange={handleChange}
                                                    min="0"
                                                    max="31"
                                                />
                                                {validationErrors.allow_late_count && (
                                                    <div className="invalid-feedback">{validationErrors.allow_late_count}</div>
                                                )}
                                                <div className="form-text">Number of allowed late arrivals per month</div>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="late_deduction_type" className="form-label fw-medium">
                                                    Late Deduction Type
                                                </label>
                                                <select
                                                    className="form-select"
                                                    id="late_deduction_type"
                                                    name="late_deduction_type"
                                                    value={formData.late_deduction_type}
                                                    onChange={handleChange}
                                                >
                                                    <option value="NONE">No Deduction</option>
                                                    <option value="HALF_DAY">Half Day Deduction</option>
                                                    <option value="LOP">Loss of Pay (LOP)</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Punch Window Settings */}
                                    <div className="card border-0 mt-3" style={{ background: '#f0f7ff' }}>
                                        <div className="card-body">
                                            <h6 className="fw-medium mb-3">
                                                <i className="bi bi-door-open me-2 text-primary"></i>
                                                Punch Window Settings
                                                <small className="text-muted fw-normal ms-2">(minutes allowed before/after shift times)</small>
                                            </h6>
                                            <div className="row g-3">
                                                <div className="col-md-3">
                                                    <label htmlFor="in_window_before" className="form-label fw-medium">
                                                        <i className="bi bi-box-arrow-in-right me-1 text-success"></i>
                                                        In — Early
                                                    </label>
                                                    <div className="input-group">
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.in_window_before ? 'is-invalid' : ''}`}
                                                            id="in_window_before"
                                                            name="in_window_before"
                                                            value={formData.in_window_before}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="240"
                                                        />
                                                        <span className="input-group-text">min</span>
                                                    </div>
                                                    {validationErrors.in_window_before && (
                                                        <div className="text-danger small mt-1">{validationErrors.in_window_before}</div>
                                                    )}
                                                    <div className="form-text">Allow punch-in this many mins early</div>
                                                </div>
                                                <div className="col-md-3">
                                                    <label htmlFor="in_window_after" className="form-label fw-medium">
                                                        <i className="bi bi-box-arrow-in-right me-1 text-danger"></i>
                                                        In — Late
                                                    </label>
                                                    <div className="input-group">
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.in_window_after ? 'is-invalid' : ''}`}
                                                            id="in_window_after"
                                                            name="in_window_after"
                                                            value={formData.in_window_after}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="240"
                                                        />
                                                        <span className="input-group-text">min</span>
                                                    </div>
                                                    {validationErrors.in_window_after && (
                                                        <div className="text-danger small mt-1">{validationErrors.in_window_after}</div>
                                                    )}
                                                    <div className="form-text">Allow punch-in this many mins late</div>
                                                </div>
                                                <div className="col-md-3">
                                                    <label htmlFor="out_window_before" className="form-label fw-medium">
                                                        <i className="bi bi-box-arrow-right me-1 text-warning"></i>
                                                        Out — Early
                                                    </label>
                                                    <div className="input-group">
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.out_window_before ? 'is-invalid' : ''}`}
                                                            id="out_window_before"
                                                            name="out_window_before"
                                                            value={formData.out_window_before}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="240"
                                                        />
                                                        <span className="input-group-text">min</span>
                                                    </div>
                                                    {validationErrors.out_window_before && (
                                                        <div className="text-danger small mt-1">{validationErrors.out_window_before}</div>
                                                    )}
                                                    <div className="form-text">Allow punch-out this many mins early</div>
                                                </div>
                                                <div className="col-md-3">
                                                    <label htmlFor="out_window_after" className="form-label fw-medium">
                                                        <i className="bi bi-box-arrow-right me-1 text-secondary"></i>
                                                        Out — Late
                                                    </label>
                                                    <div className="input-group">
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.out_window_after ? 'is-invalid' : ''}`}
                                                            id="out_window_after"
                                                            name="out_window_after"
                                                            value={formData.out_window_after}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="240"
                                                        />
                                                        <span className="input-group-text">min</span>
                                                    </div>
                                                    {validationErrors.out_window_after && (
                                                        <div className="text-danger small mt-1">{validationErrors.out_window_after}</div>
                                                    )}
                                                    <div className="form-text">Allow punch-out this many mins late</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Summary Card */}
                                    <div className="card bg-light border-0 mt-3">
                                        <div className="card-body">
                                            <h6 className="fw-medium mb-3">
                                                <i className="bi bi-info-circle me-2 text-primary"></i>
                                                Shift Summary
                                            </h6>
                                            <div className="row">
                                                <div className="col-md-3">
                                                    <small className="text-muted d-block">Duration</small>
                                                    <strong className="text-primary">
                                                        {calculateDuration(formData.start_time, formData.end_time)}
                                                    </strong>
                                                </div>
                                                <div className="col-md-3">
                                                    <small className="text-muted d-block">Working Hours</small>
                                                    <strong className="text-success">{formData.working_hours} hours</strong>
                                                </div>
                                                <div className="col-md-3">
                                                    <small className="text-muted d-block">Shift Type</small>
                                                    <span className={`badge ${getShiftTypeColor(getShiftType(formData.start_time, formData.end_time, formData.night_shift))}`}>
                                                        {getShiftType(formData.start_time, formData.end_time, formData.night_shift)} Shift
                                                        {formData.night_shift ? ' (Night)' : ''}
                                                    </span>
                                                </div>
                                                <div className="col-md-3">
                                                    <small className="text-muted d-block">Late Policy</small>
                                                    <strong className="text-warning">
                                                        {formData.allow_late_count} times / {getLateDeductionTypeLabel(formData.late_deduction_type)}
                                                    </strong>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-light border-secondary-subtle"
                                        onClick={() => setShowAddModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary text-white"
                                    >
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Add Shift
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Shift Modal */}
            {showEditModal && selectedShift && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header" style={{
                                background: 'linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)',
                                borderBottom: 'none'
                            }}>
                                <h5 className="modal-title text-white">
                                    <i className="bi bi-pencil-square me-2"></i>
                                    Edit Shift: {selectedShift.shift_name}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowEditModal(false)}
                                ></button>
                            </div>

                            <form onSubmit={handleEditSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        {/* Left Column - Basic Info */}
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="shift_name_edit" className="form-label fw-medium">
                                                    Shift Name <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${validationErrors.shift_name ? 'is-invalid' : ''}`}
                                                    id="shift_name_edit"
                                                    name="shift_name"
                                                    value={formData.shift_name}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                {validationErrors.shift_name && (
                                                    <div className="invalid-feedback">{validationErrors.shift_name}</div>
                                                )}
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="start_time_edit" className="form-label fw-medium">
                                                            Start Time <span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="time"
                                                            className={`form-control ${validationErrors.start_time ? 'is-invalid' : ''}`}
                                                            id="start_time_edit"
                                                            name="start_time"
                                                            value={formData.start_time}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                        {validationErrors.start_time && (
                                                            <div className="invalid-feedback">{validationErrors.start_time}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="end_time_edit" className="form-label fw-medium">
                                                            End Time <span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="time"
                                                            className={`form-control ${validationErrors.end_time ? 'is-invalid' : ''}`}
                                                            id="end_time_edit"
                                                            name="end_time"
                                                            value={formData.end_time}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                        {validationErrors.end_time && (
                                                            <div className="invalid-feedback">{validationErrors.end_time}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="break_mts_edit" className="form-label fw-medium">
                                                    Break Time (minutes)
                                                </label>
                                                <input
                                                    type="number"
                                                    className={`form-control ${validationErrors.break_mts ? 'is-invalid' : ''}`}
                                                    id="break_mts_edit"
                                                    name="break_mts"
                                                    value={formData.break_mts}
                                                    onChange={handleChange}
                                                    min="0"
                                                    max="480"
                                                    step="15"
                                                />
                                                {validationErrors.break_mts && (
                                                    <div className="invalid-feedback">{validationErrors.break_mts}</div>
                                                )}
                                            </div>

                                            <div className="mb-3 form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="night_shift_edit"
                                                    name="night_shift"
                                                    checked={formData.night_shift === 1}
                                                    onChange={handleChange}
                                                />
                                                <label className="form-check-label" htmlFor="night_shift_edit">
                                                    Night Shift
                                                </label>
                                            </div>
                                        </div>

                                        {/* Right Column - Configuration */}
                                        <div className="col-md-6">
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="grace_in_mts_edit" className="form-label fw-medium">
                                                            Grace In (minutes)
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.grace_in_mts ? 'is-invalid' : ''}`}
                                                            id="grace_in_mts_edit"
                                                            name="grace_in_mts"
                                                            value={formData.grace_in_mts}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="120"
                                                        />
                                                        {validationErrors.grace_in_mts && (
                                                            <div className="invalid-feedback">{validationErrors.grace_in_mts}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="grace_out_mts_edit" className="form-label fw-medium">
                                                            Grace Out (minutes)
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.grace_out_mts ? 'is-invalid' : ''}`}
                                                            id="grace_out_mts_edit"
                                                            name="grace_out_mts"
                                                            value={formData.grace_out_mts}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="120"
                                                        />
                                                        {validationErrors.grace_out_mts && (
                                                            <div className="invalid-feedback">{validationErrors.grace_out_mts}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="min_halfday_hours_edit" className="form-label fw-medium">
                                                            Min Half Day Hours
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.min_halfday_hours ? 'is-invalid' : ''}`}
                                                            id="min_halfday_hours_edit"
                                                            name="min_halfday_hours"
                                                            value={formData.min_halfday_hours}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="12"
                                                            step="0.5"
                                                        />
                                                        {validationErrors.min_halfday_hours && (
                                                            <div className="invalid-feedback">{validationErrors.min_halfday_hours}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="min_fullday_hours_edit" className="form-label fw-medium">
                                                            Min Full Day Hours
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.min_fullday_hours ? 'is-invalid' : ''}`}
                                                            id="min_fullday_hours_edit"
                                                            name="min_fullday_hours"
                                                            value={formData.min_fullday_hours}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="24"
                                                            step="1"
                                                        />
                                                        {validationErrors.min_fullday_hours && (
                                                            <div className="invalid-feedback">{validationErrors.min_fullday_hours}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="allow_late_count_edit" className="form-label fw-medium">
                                                    Allowed Late Count (per month)
                                                </label>
                                                <input
                                                    type="number"
                                                    className={`form-control ${validationErrors.allow_late_count ? 'is-invalid' : ''}`}
                                                    id="allow_late_count_edit"
                                                    name="allow_late_count"
                                                    value={formData.allow_late_count}
                                                    onChange={handleChange}
                                                    min="0"
                                                    max="31"
                                                />
                                                {validationErrors.allow_late_count && (
                                                    <div className="invalid-feedback">{validationErrors.allow_late_count}</div>
                                                )}
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="late_deduction_type_edit" className="form-label fw-medium">
                                                    Late Deduction Type
                                                </label>
                                                <select
                                                    className="form-select"
                                                    id="late_deduction_type_edit"
                                                    name="late_deduction_type"
                                                    value={formData.late_deduction_type}
                                                    onChange={handleChange}
                                                >
                                                    <option value="NONE">No Deduction</option>
                                                    <option value="HALF_DAY">Half Day Deduction</option>
                                                    <option value="LOP">Loss of Pay (LOP)</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Punch Window Settings */}
                                    <div className="card border-0 mt-3" style={{ background: '#f0f7ff' }}>
                                        <div className="card-body">
                                            <h6 className="fw-medium mb-3">
                                                <i className="bi bi-door-open me-2 text-primary"></i>
                                                Punch Window Settings
                                                <small className="text-muted fw-normal ms-2">(minutes allowed before/after shift times)</small>
                                            </h6>
                                            <div className="row g-3">
                                                <div className="col-md-3">
                                                    <label htmlFor="in_window_before_edit" className="form-label fw-medium">
                                                        <i className="bi bi-box-arrow-in-right me-1 text-success"></i>
                                                        In — Early
                                                    </label>
                                                    <div className="input-group">
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.in_window_before ? 'is-invalid' : ''}`}
                                                            id="in_window_before_edit"
                                                            name="in_window_before"
                                                            value={formData.in_window_before}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="240"
                                                        />
                                                        <span className="input-group-text">min</span>
                                                    </div>
                                                    {validationErrors.in_window_before && (
                                                        <div className="text-danger small mt-1">{validationErrors.in_window_before}</div>
                                                    )}
                                                    <div className="form-text">Allow punch-in this many mins early</div>
                                                </div>
                                                <div className="col-md-3">
                                                    <label htmlFor="in_window_after_edit" className="form-label fw-medium">
                                                        <i className="bi bi-box-arrow-in-right me-1 text-danger"></i>
                                                        In — Late
                                                    </label>
                                                    <div className="input-group">
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.in_window_after ? 'is-invalid' : ''}`}
                                                            id="in_window_after_edit"
                                                            name="in_window_after"
                                                            value={formData.in_window_after}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="240"
                                                        />
                                                        <span className="input-group-text">min</span>
                                                    </div>
                                                    {validationErrors.in_window_after && (
                                                        <div className="text-danger small mt-1">{validationErrors.in_window_after}</div>
                                                    )}
                                                    <div className="form-text">Allow punch-in this many mins late</div>
                                                </div>
                                                <div className="col-md-3">
                                                    <label htmlFor="out_window_before_edit" className="form-label fw-medium">
                                                        <i className="bi bi-box-arrow-right me-1 text-warning"></i>
                                                        Out — Early
                                                    </label>
                                                    <div className="input-group">
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.out_window_before ? 'is-invalid' : ''}`}
                                                            id="out_window_before_edit"
                                                            name="out_window_before"
                                                            value={formData.out_window_before}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="240"
                                                        />
                                                        <span className="input-group-text">min</span>
                                                    </div>
                                                    {validationErrors.out_window_before && (
                                                        <div className="text-danger small mt-1">{validationErrors.out_window_before}</div>
                                                    )}
                                                    <div className="form-text">Allow punch-out this many mins early</div>
                                                </div>
                                                <div className="col-md-3">
                                                    <label htmlFor="out_window_after_edit" className="form-label fw-medium">
                                                        <i className="bi bi-box-arrow-right me-1 text-secondary"></i>
                                                        Out — Late
                                                    </label>
                                                    <div className="input-group">
                                                        <input
                                                            type="number"
                                                            className={`form-control ${validationErrors.out_window_after ? 'is-invalid' : ''}`}
                                                            id="out_window_after_edit"
                                                            name="out_window_after"
                                                            value={formData.out_window_after}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="240"
                                                        />
                                                        <span className="input-group-text">min</span>
                                                    </div>
                                                    {validationErrors.out_window_after && (
                                                        <div className="text-danger small mt-1">{validationErrors.out_window_after}</div>
                                                    )}
                                                    <div className="form-text">Allow punch-out this many mins late</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Summary Card */}
                                    <div className="card bg-light border-0 mt-3">
                                        <div className="card-body">
                                            <h6 className="fw-medium mb-3">
                                                <i className="bi bi-info-circle me-2 text-primary"></i>
                                                Shift Summary
                                            </h6>
                                            <div className="row">
                                                <div className="col-md-3">
                                                    <small className="text-muted d-block">Duration</small>
                                                    <strong className="text-primary">
                                                        {calculateDuration(formData.start_time, formData.end_time)}
                                                    </strong>
                                                </div>
                                                <div className="col-md-3">
                                                    <small className="text-muted d-block">Working Hours</small>
                                                    <strong className="text-success">{formData.working_hours} hours</strong>
                                                </div>
                                                <div className="col-md-3">
                                                    <small className="text-muted d-block">Shift Type</small>
                                                    <span className={`badge ${getShiftTypeColor(getShiftType(formData.start_time, formData.end_time, formData.night_shift))}`}>
                                                        {getShiftType(formData.start_time, formData.end_time, formData.night_shift)} Shift
                                                        {formData.night_shift ? ' (Night)' : ''}
                                                    </span>
                                                </div>
                                                <div className="col-md-3">
                                                    <small className="text-muted d-block">Late Policy</small>
                                                    <strong className="text-warning">
                                                        {formData.allow_late_count} times / {getLateDeductionTypeLabel(formData.late_deduction_type)}
                                                    </strong>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-light border-secondary-subtle"
                                        onClick={() => setShowEditModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary text-white"
                                    >
                                        <i className="bi bi-check-circle me-2"></i>
                                        Update Shift
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedShift && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-danger text-white">
                                <h5 className="modal-title">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    Confirm Delete
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowDeleteModal(false)}
                                ></button>
                            </div>

                            <div className="modal-body">
                                <div className="alert alert-warning" role="alert">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    <strong>Warning:</strong> This action cannot be undone.
                                </div>

                                <p>Are you sure you want to delete the following shift?</p>

                                <div className="card border-0 bg-light">
                                    <div className="card-body">
                                        <h6 className="fw-bold">{selectedShift.shift_name}</h6>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <small className="text-muted d-block">Timings</small>
                                                <strong>{selectedShift.start_time.substring(0, 5)} - {selectedShift.end_time.substring(0, 5)}</strong>
                                            </div>
                                            <div className="col-md-6">
                                                <small className="text-muted d-block">Duration</small>
                                                <strong>{calculateDuration(selectedShift.start_time, selectedShift.end_time)}</strong>
                                            </div>
                                        </div>
                                        <div className="row mt-2">
                                            <div className="col-md-6">
                                                <small className="text-muted d-block">Working Hours</small>
                                                <strong>{selectedShift.working_hours} hours</strong>
                                            </div>
                                            <div className="col-md-6">
                                                <small className="text-muted d-block">Shift Type</small>
                                                <span className={`badge ${getShiftTypeColor(getShiftType(selectedShift.start_time, selectedShift.end_time, selectedShift.night_shift))}`}>
                                                    {getShiftType(selectedShift.start_time, selectedShift.end_time, selectedShift.night_shift)} Shift
                                                    {selectedShift.night_shift ? ' (Night)' : ''}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="alert alert-danger mt-3" role="alert">
                                    <i className="bi bi-info-circle me-2"></i>
                                    <strong>Note:</strong> Deleting this shift may affect employee schedules.
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light border-secondary-subtle"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleDeleteConfirm}
                                >
                                    <i className="bi bi-trash me-2"></i>
                                    Delete Shift
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Shift;